import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, forkJoin } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ChartOfAccounts } from '../chart-of-accounts/chart-of-accounts.model';
import { ChartOfAccountsService } from '../chart-of-accounts/chart-of-accounts.service';
import { Company } from '../company/company.model';
import { CompanyService } from '../company/company.service';
import { Customer } from '../customer/customer.model';
import { CustomerService } from '../customer/customer.service';
import { APIListWrapper } from '../models/common/api-list-wrapper.model';
import { app_permissions } from '../security/app-permissions';
import { PermissionService } from '../security/permission.service';
import { SessionStorageService } from '../services/session-storage.service';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../shared/page-layout/page-search-panel.component';
import { Expense } from './expense.model';
import { ExpenseService } from './expense.service';

@Component({
    selector: 'app-expense',
    standalone: true,
    templateUrl: './expense.component.html',
    styleUrls: ['./expense.component.css'],
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        ToastModule,
        ButtonModule,
        TableModule,
        BreadcrumbModule,
        InputTextModule,
        TagModule,
        ProgressBarModule,
        ConfirmDialogModule,
        TooltipModule,
        SelectModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        PageMetricStripComponent,
        PageSearchPanelComponent,
        PageDataPanelComponent
    ],
    providers: [
        MessageService,
        ConfirmationService,
        ExpenseService,
        CustomerService,
        CompanyService,
        ChartOfAccountsService,
        SessionStorageService
    ]
})
export class ExpenseComponent implements OnInit, OnDestroy {
    private readonly ngZone = inject(NgZone);
    private readonly cdr = inject(ChangeDetectorRef);

    canViewExpenses = false;
    canCreateExpense = false;
    canEditExpense = false;
    canDeleteExpense = false;
    canUploadExpenseAttachment = false;

    expenses: Expense[] = [];
    private rawExpenses: Expense[] = [];
    isLoading = false;
    totalRecords = 0;
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    lastUpdated: Date | null = null;

    searchText = '';
    descriptionFilter = '';
    employeeFilter = '';
    vendorFilter = '';
    statusFilter = '';

    totalToReport = 0;
    totalUnderValidation = 0;
    totalToBeReimbursed = 0;
    totalVisibleSpend = 0;

    readonly statusOptions = [
        { label: 'Draft', value: 'Draft' },
        { label: 'Submitted', value: 'Submitted' },
        { label: 'Pending Approval', value: 'Pending Approval' },
        { label: 'Approved', value: 'Approved' },
        { label: 'Posted', value: 'Posted' },
        { label: 'Rejected', value: 'Rejected' },
        { label: 'Refused', value: 'Refused' }
    ];

    private readonly destroy$ = new Subject<void>();
    private customers: Customer[] = [];
    private companies: Company[] = [];
    private chartOfAccounts: ChartOfAccounts[] = [];
    private pendingLoadRequestKey: string | null = null;
    lastLazyEvent: TableLazyLoadEvent = { first: 0, rows: 10 };

    constructor(
        private router: Router,
        private expenseService: ExpenseService,
        private customerService: CustomerService,
        private companyService: CompanyService,
        private chartOfAccountsService: ChartOfAccountsService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private permissionService: PermissionService
    ) {}

    ngOnInit(): void {
        this.canViewExpenses = this.permissionService.hasPermission(app_permissions.expense.records.view);
        this.canCreateExpense = this.permissionService.hasPermission(app_permissions.expense.records.create);
        this.canEditExpense = this.permissionService.hasPermission(app_permissions.expense.records.edit);
        this.canDeleteExpense = this.permissionService.hasPermission(app_permissions.expense.records.delete);
        this.canUploadExpenseAttachment = this.permissionService.hasPermission(app_permissions.expense.attachments.upload);

        this.breadcrumbItems = [
            { label: 'Finance', routerLink: '/expense' },
            { label: 'Expenses', routerLink: '/expense' }
        ];

        if (!this.canViewExpenses) {
            return;
        }

        this.loadInitialData();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private syncView(work?: () => void): void {
        this.ngZone.run(() => {
            work?.();
            this.cdr.detectChanges();
        });
    }

    private loadInitialData(): void {
        this.isLoading = true;
        const companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        const countryId = this.sessionStorageService.getCompany()?.countryId ?? 0;

        forkJoin({
            customers: this.customerService.getCustomersByCompany(companyId),
            companies: this.companyService.getCompaniesByCountry(countryId),
            accounts: this.chartOfAccountsService.getAllChartOfAccountsByCompany(companyId, false)
        }).pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({ customers, companies, accounts }) => {
                    this.syncView(() => {
                        this.customers = customers;
                        this.companies = companies;
                        this.chartOfAccounts = accounts;
                    });
                    this.loadExpensesLazy(this.lastLazyEvent);
                },
                error: () => {
                    this.syncView(() => {
                        this.messageService.add({ severity: 'error', summary: 'Expenses', detail: 'Failed to load the finance workspace data.' });
                        this.isLoading = false;
                    });
                }
            });
    }

    loadExpenses(): void {
        this.loadExpensesLazy(this.lastLazyEvent);
    }

    loadExpensesLazy(event?: TableLazyLoadEvent): void {
        if (!this.canViewExpenses) {
            return;
        }

        this.lastLazyEvent = {
            first: event?.first ?? this.lastLazyEvent.first ?? 0,
            rows: event?.rows ?? this.lastLazyEvent.rows ?? 10
        };

        const owningCompanyId = this.sessionStorageService.getCompany()?.id;
        if (!owningCompanyId) {
            this.messageService.add({ severity: 'warn', summary: 'Expenses', detail: 'No company selected. Expenses cannot be loaded.' });
            this.isLoading = false;
            return;
        }

        const page = Math.floor((this.lastLazyEvent.first ?? 0) / (this.lastLazyEvent.rows ?? 10));
        const pageSize = this.lastLazyEvent.rows ?? 10;
        const searchText = this.searchText.trim();
        const requestKey = `${owningCompanyId}|company|${page}|${pageSize}|${searchText}`;

        if (this.pendingLoadRequestKey === requestKey) {
            return;
        }

        this.pendingLoadRequestKey = requestKey;
        this.isLoading = true;

        this.expenseService.getAllExpenses(owningCompanyId, false, page, pageSize, searchText || undefined)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.syncView(() => {
                        this.pendingLoadRequestKey = null;
                        this.isLoading = false;
                    });
                })
            )
            .subscribe({
                next: (response: APIListWrapper<Expense>) => {
                    this.syncView(() => {
                        this.rawExpenses = (response.items || []).map(expense => this.hydrateExpenseForDisplay(expense));
                        this.totalRecords = response.count || 0;
                        this.calculateSummaryTotals();
                        this.applyLocalFilters(false);
                        this.lastUpdated = new Date();
                    });
                },
                error: () => {
                    this.syncView(() => {
                        this.messageService.add({ severity: 'error', summary: 'Expenses', detail: 'Failed to load expenses.' });
                    });
                }
            });
    }

    applySearch(): void {
        this.lastLazyEvent = { ...this.lastLazyEvent, first: 0 };
        this.loadExpensesLazy(this.lastLazyEvent);
    }

    clearSearch(): void {
        this.searchText = '';
        this.descriptionFilter = '';
        this.employeeFilter = '';
        this.vendorFilter = '';
        this.statusFilter = '';
        this.lastLazyEvent = { ...this.lastLazyEvent, first: 0 };
        this.applyLocalFilters(false);
        this.loadExpensesLazy(this.lastLazyEvent);
    }

    openNew(): void {
        this.router.navigate(['/expense/add']);
    }

    viewExpense(expense: Expense): void {
        if (expense.expenseId) {
            this.router.navigate(['/expense/view', expense.expenseId]);
        }
    }

    editExpense(expense: Expense): void {
        if (expense.expenseId && this.canEditExpense) {
            this.router.navigate(['/expense/edit', expense.expenseId]);
        }
    }

    deleteExpense(expense: Expense): void {
        if (!expense.expenseId || !this.canDeleteExpense) {
            return;
        }

        this.confirmationService.confirm({
            message: `Delete expense <b>${expense.expenseNumber || expense.description || '#' + expense.expenseId}</b>?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-circle',
            acceptButtonStyleClass: 'p-button-danger p-button-text',
            rejectButtonStyleClass: 'p-button-text p-button-secondary',
            accept: () => {
                this.expenseService.deleteExpense(expense.expenseId!)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => {
                            this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Expense removed successfully.' });
                            this.loadExpensesLazy(this.lastLazyEvent);
                        },
                        error: () => {
                            this.messageService.add({ severity: 'error', summary: 'Expenses', detail: 'Unable to delete the expense.' });
                        }
                    });
            }
        });
    }

    getStatusSeverity(status?: string): string {
        switch (status) {
            case 'Approved':
            case 'Posted':
                return 'success';
            case 'Pending Approval':
            case 'Under Validation':
            case 'Submitted':
                return 'warning';
            case 'Rejected':
            case 'Refused':
                return 'danger';
            case 'Draft':
                return 'info';
            default:
                return 'secondary';
        }
    }

    hasTaxes(expense: Expense): boolean {
        return !!expense.items?.some(item => (item.taxAmount || 0) > 0);
    }

    getClientDisplayName(expense: Expense): string {
        return expense.crmDisplayName
            || expense.crmClient?.companyName
            || expense.crmClient?.customerNameLocal
            || expense.partyDisplayName
            || expense.vendor?.name
            || 'No party';
    }

    getContactDisplayName(expense: Expense): string {
        return expense.crmContactDisplayName
            || expense.crmClientContact?.name
            || expense.crmContactEmail
            || expense.crmContactPhone
            || expense.partyContactDisplayName
            || '-';
    }

    getTotalTaxAmount(expense: Expense): number {
        return expense.items?.reduce((sum, item) => sum + (item.taxAmount || 0), 0) || 0;
    }

    getMetrics(): PageMetric[] {
        return [
            { label: 'Visible spend', value: this.formatCurrency(this.totalVisibleSpend, this.displayCurrency), tone: 'info', hint: `${this.expenses.length} rows on screen` },
            { label: 'Draft value', value: this.formatCurrency(this.totalToReport, this.displayCurrency), hint: 'Ready to submit' },
            { label: 'Awaiting approval', value: this.formatCurrency(this.totalUnderValidation, this.displayCurrency), tone: 'warn', hint: 'Submitted and pending' },
            { label: 'Approved value', value: this.formatCurrency(this.totalToBeReimbursed, this.displayCurrency), tone: 'success', hint: 'Ready for posting / reimbursement' }
        ];
    }

    get activeSearchCount(): number {
        return [
            this.searchText,
            this.descriptionFilter,
            this.employeeFilter,
            this.vendorFilter,
            this.statusFilter
        ].filter(value => !!String(value || '').trim()).length;
    }

    get lastUpdatedText(): string {
        return this.lastUpdated
            ? `Updated ${this.lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
            : '';
    }

    get displayCurrency(): string {
        return this.rawExpenses.find(expense => expense.currencyCode)?.currencyCode || 'SAR';
    }

    private hydrateExpenseForDisplay(expense: Expense): Expense {
        if (expense.submittedBy?.id) {
            const customer = this.customers.find(candidate => candidate.id === expense.submittedBy.id);
            if (customer) {
                expense.submittedBy.name = customer.name;
            }
        }

        if (expense.owningCompany?.id) {
            const company = this.companies.find(candidate => candidate.id === expense.owningCompany.id);
            if (company) {
                expense.owningCompany.name = company.name;
            }
        }

        if (expense.vendor?.id) {
            const vendorId = expense.vendor.id;
            const vendor = this.companies.find(candidate => candidate.id === vendorId);
            if (vendor) {
                expense.vendor.name = vendor.name;
            }
        }

        expense.items?.forEach(item => {
            if (item.account?.accountId) {
                const account = this.chartOfAccounts.find(candidate => candidate.accountId === item.account.accountId);
                if (account) {
                    item.account.accountName = account.accountName;
                }
            }
        });

        return expense;
    }

    private calculateSummaryTotals(): void {
        this.totalToReport = this.rawExpenses
            .filter(expense => expense.status === 'Draft')
            .reduce((sum, expense) => sum + (expense.totalAmount || 0), 0);

        this.totalUnderValidation = this.rawExpenses
            .filter(expense => expense.status === 'Submitted' || expense.status === 'Pending Approval' || expense.status === 'Under Validation')
            .reduce((sum, expense) => sum + (expense.totalAmount || 0), 0);

        this.totalToBeReimbursed = this.rawExpenses
            .filter(expense => expense.status === 'Approved')
            .reduce((sum, expense) => sum + (expense.totalAmount || 0), 0);
    }

    private applyLocalFilters(resetFirst: boolean): void {
        const globalSearch = this.normalize(this.searchText);
        const descriptionFilter = this.normalize(this.descriptionFilter);
        const employeeFilter = this.normalize(this.employeeFilter);
        const vendorFilter = this.normalize(this.vendorFilter);
        const statusFilter = this.normalize(this.statusFilter);

        this.expenses = this.rawExpenses.filter(expense => {
            const globalMatches = !globalSearch || [
                expense.expenseNumber,
                expense.description,
                this.getClientDisplayName(expense),
                this.getContactDisplayName(expense),
                expense.submittedBy?.name,
                expense.owningCompany?.name,
                expense.vendor?.name,
                expense.status,
                expense.notes
            ].some(value => this.matches(value, globalSearch));

            const descriptionMatches = !descriptionFilter || this.matches(expense.description, descriptionFilter);
            const employeeMatches = !employeeFilter || this.matches(expense.submittedBy?.name, employeeFilter);
            const vendorMatches = !vendorFilter
                || this.matches(this.getClientDisplayName(expense), vendorFilter)
                || this.matches(this.getContactDisplayName(expense), vendorFilter)
                || this.matches(expense.vendor?.name, vendorFilter)
                || this.matches(expense.owningCompany?.name, vendorFilter);
            const statusMatches = !statusFilter || this.matches(expense.status, statusFilter);

            return globalMatches && descriptionMatches && employeeMatches && vendorMatches && statusMatches;
        });

        this.totalVisibleSpend = this.expenses.reduce((sum, expense) => sum + (expense.totalAmount || 0), 0);

        if (resetFirst) {
            this.lastLazyEvent = { ...this.lastLazyEvent, first: 0 };
        }
    }

    private matches(value: unknown, query: string): boolean {
        return this.normalize(value).includes(query);
    }

    private normalize(value: unknown): string {
        return String(value ?? '').trim().toLowerCase();
    }

    private formatCurrency(value: number, currencyCode: string): string {
        return new Intl.NumberFormat('en-SA', {
            style: 'currency',
            currency: currencyCode || 'SAR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value || 0);
    }
}
